import { captureClientException } from './posthogErrorTracking';

type FileExtension = 'pdf' | 'xlsx' | 'csv';

/**
 * Downloads a file with the provided data and specified file type.
 * @param {string} data - The base64-encoded file data.
 * @param {('pdf' | 'xlsx' | 'csv')} extension - The file extension.
 * @param {string} filenameWithoutFileExtension - The desired filename without the file extension.
 */
export function downloadFile(data: string, extension: FileExtension, filenameWithoutFileExtension: string) {
  let mimeType = '';

  if (extension === 'csv') {
    mimeType = `data:text/csv;charset=utf-8,${encodeURIComponent(data)}`;
  } else if (extension === 'xlsx') {
    mimeType = `data:application/vnd.ms-excel;base64,${encodeURIComponent(data)}`;
  } else {
    mimeType = `data:application/${extension};base64,${encodeURIComponent(data)}`;
  }

  const link = document.createElement('a');
  link.href = mimeType;
  link.download = `${filenameWithoutFileExtension}.${extension}`;

  // Append the link to the document and simulate a click on it to start the download.
  document.body.appendChild(link);
  link.click();

  // Remove the link from the document and revoke the URL to free up resources.
  document.body.removeChild(link);
  URL.revokeObjectURL(mimeType);
}

/**
 * Reads the filename a presigned URL asks the object to be saved as
 * (`response-content-disposition`). Downloads that go through `downloadFileUri` fetch
 * the URL as a blob, so the browser never applies that header itself — and the name
 * lives on the server, which is the only place that knows the export naming rules.
 */
export function getAttachmentFilenameFromUrl(url: string): string | null {
  const disposition = new URL(url, window.location.origin).searchParams.get('response-content-disposition');
  const filename = disposition?.match(/filename="([^"]+)"/)?.[1];

  return filename ?? null;
}

/**
 * Downloads a file from the specified URL.
 * @async
 * @param {string} url - The URL of the file to be downloaded.
 * @param {string} filename - The name to be given to the downloaded file.
 * @returns {Promise<void>} A Promise that resolves when the download is complete.
 * @throws {Error} If the download fails or encounters an error.
 */
export async function downloadFileUri(url: string, filename: string): Promise<void> {
  try {
    const response = await fetch(url);

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.blob();

    const link = document.createElement('a');
    link.href = URL.createObjectURL(data);
    link.download = filename;
    link.click();
    URL.revokeObjectURL(link.href);
  } catch (error) {
    captureClientException(error, { source: 'download_file_uri_failed' });
    throw error;
  }
}
